import React, { useState, useEffect } from 'react';

const TaxRateFormModal = ({ taxRate, jurisdictions, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    taxName: '',
    taxCode: '',
    jurisdictionId: '',
    taxRate: '',
    calculationMethod: 'percentage',
    appliesTo: [],
    exemptionRules: {},
    effectiveDate: '',
    expirationDate: '',
    reverseChargeEnabled: false,
    multiCurrencyEnabled: false,
    notes: '',
    status: 'active'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (taxRate) {
      setFormData({
        taxName: taxRate?.taxName || '',
        taxCode: taxRate?.taxCode || '',
        jurisdictionId: taxRate?.jurisdictionId || '',
        taxRate: taxRate?.taxRate ? (taxRate?.taxRate * 100)?.toString() : '', // Convert to percentage for display
        calculationMethod: taxRate?.calculationMethod || 'percentage',
        appliesTo: taxRate?.appliesTo || [],
        exemptionRules: taxRate?.exemptionRules || {},
        effectiveDate: taxRate?.effectiveDate || '',
        expirationDate: taxRate?.expirationDate || '',
        reverseChargeEnabled: taxRate?.reverseChargeEnabled || false,
        multiCurrencyEnabled: taxRate?.multiCurrencyEnabled || false,
        notes: taxRate?.notes || '',
        status: taxRate?.status || 'active'
      });
    }
  }, [taxRate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.taxName?.trim()) {
      newErrors.taxName = 'Tax name is required';
    }

    if (!formData?.taxCode?.trim()) {
      newErrors.taxCode = 'Tax code is required';
    }

    if (!formData?.jurisdictionId) {
      newErrors.jurisdictionId = 'Jurisdiction is required';
    }

    if (!formData?.taxRate?.trim()) {
      newErrors.taxRate = 'Tax rate is required';
    } else {
      const rate = parseFloat(formData?.taxRate);
      if (isNaN(rate) || rate < 0 || rate > 100) {
        newErrors.taxRate = 'Tax rate must be between 0 and 100 percent';
      }
    }

    if (!formData?.effectiveDate) {
      newErrors.effectiveDate = 'Effective date is required';
    }

    if (formData?.expirationDate && formData?.effectiveDate) {
      if (new Date(formData.expirationDate) <= new Date(formData.effectiveDate)) {
        newErrors.expirationDate = 'Expiration date must be after effective date';
      }
    }

    if (formData?.appliesTo?.length === 0) {
      newErrors.appliesTo = 'At least one application type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        taxRate: parseFloat(formData?.taxRate) / 100, // Convert percentage to decimal
        exemptionRules: formData?.exemptionRules || {}
      };

      await onSubmit(submitData);
    } catch (err) {
      console.error('Error submitting tax rate:', err);
      setErrors({ submit: err?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAppliesToChange = (type, checked) => {
    setFormData(prev => ({
      ...prev,
      appliesTo: checked 
        ? [...prev?.appliesTo, type]
        : prev?.appliesTo?.filter(t => t !== type)
    }));
  };

  const applicationTypes = [
    { value: 'sales', label: 'Sales' },
    { value: 'services', label: 'Services' },
    { value: 'goods', label: 'Goods' },
    { value: 'digital', label: 'Digital Products' },
    { value: 'imports', label: 'Imports' },
    { value: 'exports', label: 'Exports' }
  ];

  const calculationMethods = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'fixed_amount', label: 'Fixed Amount' },
    { value: 'tiered', label: 'Tiered' },
    { value: 'compound', label: 'Compound' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {taxRate ? 'Edit Tax Rate' : 'Create New Tax Rate'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            ❌
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="taxName" className="block text-sm font-medium text-gray-700 mb-2">
                Tax Name *
              </label>
              <input
                type="text"
                id="taxName"
                value={formData?.taxName}
                onChange={(e) => handleInputChange('taxName', e?.target?.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                  errors?.taxName 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
                placeholder="e.g., California State Sales Tax"
              />
              {errors?.taxName && (
                <p className="mt-1 text-sm text-red-600">{errors?.taxName}</p>
              )}
            </div>

            <div>
              <label htmlFor="taxCode" className="block text-sm font-medium text-gray-700 mb-2">
                Tax Code *
              </label>
              <input
                type="text"
                id="taxCode"
                value={formData?.taxCode}
                onChange={(e) => handleInputChange('taxCode', e?.target?.value?.toUpperCase())}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                  errors?.taxCode 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
                placeholder="e.g., CA_STATE_SALES"
              />
              {errors?.taxCode && (
                <p className="mt-1 text-sm text-red-600">{errors?.taxCode}</p>
              )}
            </div>

            <div>
              <label htmlFor="jurisdictionId" className="block text-sm font-medium text-gray-700 mb-2">
                Jurisdiction *
              </label>
              <select
                id="jurisdictionId"
                value={formData?.jurisdictionId}
                onChange={(e) => handleInputChange('jurisdictionId', e?.target?.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                  errors?.jurisdictionId 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              >
                <option value="">Select a jurisdiction</option>
                {jurisdictions?.map((jurisdiction) => (
                  <option key={jurisdiction?.id} value={jurisdiction?.id}>
                    {jurisdiction?.name} ({jurisdiction?.jurisdictionType})
                  </option>
                ))}
              </select>
              {errors?.jurisdictionId && (
                <p className="mt-1 text-sm text-red-600">{errors?.jurisdictionId}</p>
              )}
            </div>

            <div>
              <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 mb-2">
                Tax Rate (%) *
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                max="100"
                id="taxRate"
                value={formData?.taxRate}
                onChange={(e) => handleInputChange('taxRate', e?.target?.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                  errors?.taxRate 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
                placeholder="e.g., 7.25"
              />
              {errors?.taxRate && (
                <p className="mt-1 text-sm text-red-600">{errors?.taxRate}</p>
              )}
            </div>

            <div>
              <label htmlFor="calculationMethod" className="block text-sm font-medium text-gray-700 mb-2">
                Calculation Method
              </label>
              <select
                id="calculationMethod"
                value={formData?.calculationMethod}
                onChange={(e) => handleInputChange('calculationMethod', e?.target?.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {calculationMethods?.map((method) => (
                  <option key={method?.value} value={method?.value}>
                    {method?.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                id="status"
                value={formData?.status}
                onChange={(e) => handleInputChange('status', e?.target?.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Applies To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Applies To *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {applicationTypes?.map((type) => (
                <label key={type?.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData?.appliesTo?.includes(type?.value)}
                    onChange={(e) => handleAppliesToChange(type?.value, e?.target?.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{type?.label}</span>
                </label>
              ))}
            </div>
            {errors?.appliesTo && (
              <p className="mt-1 text-sm text-red-600">{errors?.appliesTo}</p>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700 mb-2">
                Effective Date *
              </label>
              <input
                type="date"
                id="effectiveDate"
                value={formData?.effectiveDate}
                onChange={(e) => handleInputChange('effectiveDate', e?.target?.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                  errors?.effectiveDate 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {errors?.effectiveDate && (
                <p className="mt-1 text-sm text-red-600">{errors?.effectiveDate}</p>
              )}
            </div>

            <div>
              <label htmlFor="expirationDate" className="block text-sm font-medium text-gray-700 mb-2">
                Expiration Date (Optional)
              </label>
              <input
                type="date"
                id="expirationDate"
                value={formData?.expirationDate}
                onChange={(e) => handleInputChange('expirationDate', e?.target?.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                  errors?.expirationDate 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              />
              {errors?.expirationDate && (
                <p className="mt-1 text-sm text-red-600">{errors?.expirationDate}</p>
              )}
            </div>
          </div>

          {/* Advanced Options */}
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="reverseChargeEnabled"
                checked={formData?.reverseChargeEnabled}
                onChange={(e) => handleInputChange('reverseChargeEnabled', e?.target?.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="reverseChargeEnabled" className="ml-2 text-sm text-gray-700">
                Enable Reverse Charge Mechanism
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="multiCurrencyEnabled"
                checked={formData?.multiCurrencyEnabled}
                onChange={(e) => handleInputChange('multiCurrencyEnabled', e?.target?.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="multiCurrencyEnabled" className="ml-2 text-sm text-gray-700">
                Enable Multi-Currency Tax Calculations
              </label>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              id="notes"
              rows={3}
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about this tax rate..."
            />
          </div>

          {/* Submit Error */}
          {errors?.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{errors?.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' :'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Saving...' : (taxRate ? 'Update Tax Rate' : 'Create Tax Rate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaxRateFormModal;